/** @format */

import {useCallback, useEffect} from 'react';

import {Box} from '@mui/material';

import {AiMonitoringConfig} from 'graphql/generated/types';

import {TanStackTableData} from 'components/Table/core/types';
import {useCompanyContext} from 'contexts/CompanyContext';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import {ROUTES} from 'constants/ROUTES';

import {FormDataOptions} from '../../types';
import {ContentContainer} from '../../styles';
import BriefReports from '../BriefReports';
import MonitoringForm from './MonitoringForm';
import MonitoringStub from './MonitoringStub';
import {useMonitoringData} from './hooks/useMonitoringData';

interface MonitoringTabProps {
  formDataOptions: FormDataOptions;
  monitoringConfig: AiMonitoringConfig | null;
  monitoringReports: TanStackTableData[];
  refetchData: () => Promise<void>;
  needToConnectMetrika: boolean;
}

const MonitoringTab = ({
  formDataOptions,
  monitoringConfig,
  monitoringReports,
  refetchData,
  needToConnectMetrika,
}: MonitoringTabProps) => {
  const {mixpanel} = useMixpanelContext();
  const {currentCompany} = useCompanyContext();

  useEffect(() => {
    mixpanel.track('ai_monitoring_form_open', {
      project_uuid: currentCompany?.uuid,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGoToSettings = useCallback(() => {
    window.location.assign(`${ROUTES.HOME.PROJECT.EDIT}?companyId=${currentCompany?.id}`);
  }, [currentCompany?.id]);

  const {startMonitoring, stopMonitoring, updateSettings, mutationLoading} = useMonitoringData({
    refetchData,
    goalOptions: formDataOptions.goalOptions,
  });

  const handleReportView = useCallback(
    (reportId: string) => {
      mixpanel.track('ai_monitoring_result_view', {
        project_uuid: currentCompany?.uuid,
        report_id: reportId,
      });
    },
    [mixpanel, currentCompany?.uuid],
  );

  return (
    <ContentContainer sx={{'& > *:first-of-type': {borderTopLeftRadius: 0}}}>
      <MonitoringForm
        config={monitoringConfig}
        formDataOptions={formDataOptions}
        onStart={startMonitoring}
        onStop={stopMonitoring}
        onUpdate={updateSettings}
        mutationLoading={mutationLoading}
        hasReports={monitoringReports.length > 0}
        needToConnectMetrika={needToConnectMetrika}
        onGoToSettings={handleGoToSettings}
      />
      <Box sx={{flex: 1, display: 'flex'}}>
        {monitoringReports.length > 0 ? (
          <BriefReports data={monitoringReports} onReportView={handleReportView} />
        ) : (
          <MonitoringStub />
        )}
      </Box>
    </ContentContainer>
  );
};

export default MonitoringTab;
